import React, { useEffect, useState, useCallback, ChangeEvent } from 'react'
import { useDispatch } from 'react-redux'
import Icon from '@mdi/react'
import { mdiPlusCircle, mdiMinusCircleOutline, mdiCogOutline } from '@mdi/js'
import { CircleSpinner } from 'react-spinners-kit'
import { FormGroup, FormControlLabel, Checkbox } from '@mui/material'

import Section, { SectionBox, SectionOption } from '../../../../components/Section'
import List, { ListSection, ListRow, ListColumn, ListOption } from '../../../../components/List'
import Modal from '../../../../components/Modal'
import { MagicSelectForm } from '../../../../components/Select/MagicSelectForm'

import { useAppSelector } from '../../../../store/hooks'

import { ClienteType } from '../Parametro/Types/ClienteType'
import { Parametros, ResultadoApiBuscaParametro, RamoAtiviadeParametroResultado } from '../RamoAtividade/types'
import { getApi, postApi } from '../../../../utils/requestApi'
import { DOMAIN } from '../../../../var_env.'
import getMonitoramentoFrequente from './scripts/getMonitoramentoFrequente'
import getClients from '../Cliente/scripts/getClients'
import addMonitoramentoFrequente from './scripts/addMonitoramentoFrequente'
import removeMonitoramentoFrequente from './scripts/removeMonitoramentoFrequente'


type MagicOption = {
    value: string;
    label: string;
}

interface PessoaParametroResultado extends Omit<RamoAtiviadeParametroResultado, 'idRamoAtividade'> {
    idPessoa: number;
}

interface ResultadoApiPessoaParametro {
    flagErro: boolean;
    listaMensagens: string[];
    listaResultados: PessoaParametroResultado[];
}

type PessoaParametroFormProps = {
    pessoa: ClienteType;
    onSaved: () => void | Promise<void>;
}

const PessoaParametroForm: React.FC<PessoaParametroFormProps> = ({ pessoa, onSaved }) => {
    const [descricaoPessoal, setDescricaoPessoal] = useState<string>(pessoa.descricaoPessoal ?? '')
    const [listaParametros, setListaParametros] = useState<Parametros[]>([])
    const [selecionados, setSelecionados] = useState<number[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    const carregarDados = useCallback(async () => {
        if (!pessoa?.id) return

        try {
            setLoading(true)

            const responseParametros: ResultadoApiBuscaParametro = await getApi({
                endpoint: `${DOMAIN}/`,
                params: 'parametro'
            })

            if (!responseParametros.flagErro) {
                setListaParametros(responseParametros.listaResultados ?? [])
            }

            const responsePessoaParametro: ResultadoApiPessoaParametro = await getApi({
                endpoint: `${DOMAIN}/`,
                params: `pessoa-parametro?idPessoa=${pessoa.id}`
            })

            if (!responsePessoaParametro.flagErro) {
                const idsSelecionados = responsePessoaParametro.listaResultados?.map(item => item.idParametro) ?? []
                setSelecionados(idsSelecionados)
            }
        } finally {
            setLoading(false)
        }
    }, [pessoa])

    useEffect(() => {
        setDescricaoPessoal(pessoa.descricaoPessoal ?? '')
        setSelecionados([])
        carregarDados()
    }, [pessoa, carregarDados])

    const handleEditarCheck = useCallback(
        (event: ChangeEvent<HTMLInputElement>, idParametro: number) => {
            const checked = event.target.checked
            setSelecionados(prev => {
                if (checked) {
                    if (prev.includes(idParametro)) return prev
                    return [...prev, idParametro]
                }

                return prev.filter(id => id !== idParametro)
            })
        },
        []
    )

    const handleSalvar = useCallback(async () => {
        const payload = {
            id: pessoa.id,
            descricaoPessoal,
            listaParametros: selecionados.map(idParametro => ({ idParametro }))
        }

        try {
            const response = await postApi({
                endpoint: `${DOMAIN}/pessoa-monitoramento-frequente-update`,
                data: payload
            })

            if (response && !response.flagErro) {
                await onSaved()
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Erro ao atualizar pessoa-monitoramento-frequente:', error)
        }
    }, [pessoa.id, descricaoPessoal, selecionados, onSaved])

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <label
                            htmlFor="descricaoPessoal"
                            style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}
                        >
                            Período (mês)
                        </label>
                        <input
                            id="descricaoPessoal"
                            style={{ width: '100%', minHeight: 20, fontSize: '0.9rem', padding: 8 }}
                            value={descricaoPessoal}
                            onChange={e => setDescricaoPessoal(e.target.value)}
                        />
                    </div>
                
                    <SectionOption
                                icon={mdiPlusCircle}
                                tooltip="Atualizar"
                                onClick={() => handleSalvar()}
                            />
                </div>
            </div>

            <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: '0.85rem', marginBottom: 8 }}>Parâmetros</p>
                {loading && (
                    <div className="center" style={{ padding: '16px 0' }}>
                        <CircleSpinner size={24} color="grey" />
                    </div>
                )}
                {!loading && (
                    <FormGroup>
                        <List name="parametrosPessoa">
                            <ListSection>
                                {listaParametros.map((parametro, index) => (
                                    <ListRow
                                        key={parametro.id}
                                        grid="1fr"
                                        zebra={index % 2 === 0}
                                    >
                                        <ListColumn>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selecionados.includes(parametro.id)}
                                                        onChange={e =>
                                                            handleEditarCheck(e, parametro.id)
                                                        }
                                                    />
                                                }
                                                label={parametro.descricao}
                                            />
                                        </ListColumn>
                                    </ListRow>
                                ))}
                            </ListSection>
                        </List>
                    </FormGroup>
                )}
            </div>
        </div>
    )
}

const IndustriasMonitoradasFrequentes: React.FC = () => {

    const dispatch = useDispatch()

    const [listaIndustria, setListaIndustria] = useState<ClienteType[]>([])
    const [loadingIndustria, setLoadingIndustria] = useState<boolean>(false)

    const [openModal, setOpenModal] = useState<boolean>(false)
    const [selectedPessoas, setSelectedPessoas] = useState<MagicOption[]>([])

    const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false)
    const [clienteSelecionado, setClienteSelecionado] = useState<ClienteType | null>(null)

    const [openParametrosModal, setOpenParametrosModal] = useState<boolean>(false)
    const [clienteParametroSelecionado, setClienteParametroSelecionado] = useState<ClienteType | null>(null)

    const { clients, requestGet } = useAppSelector(state => state.ClientsReducer)

    const reloadMonitoramento = useCallback(async () => {
        try {
            setLoadingIndustria(true)
            const result = await getMonitoramentoFrequente()
            setListaIndustria(result)
        } finally {
            setLoadingIndustria(false)
        }
    }, [])

    useEffect(() => {
        ;(async () => {
            await reloadMonitoramento()
        })()
    }, [reloadMonitoramento])

    useEffect(() => {
        ;(async () => {
            await getClients('PessoaMonitoramentoFrequente', dispatch)
        })()
    }, [dispatch])

    const grids = '1fr 160px 160px 110px'

    const optionsPessoas: MagicOption[] =
        clients?.filter(pessoa => pessoa.flagMonitoramentoFrequente !== 1)?.map(pessoa => ({
            value: String(pessoa.id),
            label: pessoa.nome
        })) ?? []

    const SHOW_LISTA = listaIndustria && !loadingIndustria
    const SHOW_LOADING = loadingIndustria

    const handleAddSelecionados = useCallback(async () => {
        try {
            const ids = selectedPessoas
                .map(item => Number(item.value))
                .filter(id => !Number.isNaN(id))

            if (ids.length === 0) {
                setOpenModal(false)
                return
            }

            await addMonitoramentoFrequente(ids)

            setSelectedPessoas([])
            setOpenModal(false)

            await reloadMonitoramento()
            await getClients('PessoaMonitoramentoFrequente', dispatch)
        } catch (error) {
            console.error('Erro ao adicionar monitoramento frequente:', error)
        }
    }, [selectedPessoas, reloadMonitoramento, dispatch])

    const handleOpenConfirmRemover = useCallback((cliente: ClienteType) => {
        setClienteSelecionado(cliente)
        setOpenConfirmModal(true)
    }, [])

    const handleCancelRemover = useCallback(() => {
        setOpenConfirmModal(false)
        setClienteSelecionado(null)
    }, [])

    const handleConfirmRemover = useCallback(async () => {
        if (!clienteSelecionado) return

        try {
            await removeMonitoramentoFrequente(clienteSelecionado.id)
            setOpenConfirmModal(false)
            setClienteSelecionado(null)

            await reloadMonitoramento()
            await getClients('PessoaMonitoramentoFrequente', dispatch)
        } catch (error) {
            console.error('Erro ao remover monitoramento frequente:', error)
        }
    }, [clienteSelecionado, reloadMonitoramento, dispatch])

    const handleOpenParametros = useCallback((cliente: ClienteType) => {
        setClienteParametroSelecionado(cliente)
        setOpenParametrosModal(true)
    }, [])

    return (
        <>
            <Section name="IndustriasMonitoradasFrequentes">
                <SectionBox
                    title="Indústrias monitoradas frequentemente"
                    padding={false}
                    right={
                        <>
                            <SectionOption
                                icon={mdiPlusCircle}
                                tooltip="Adicionar"
                                onClick={() => setOpenModal(true)}
                            />
                        </>
                    }
                >
                    <List name="industriasMonitoradasFrequentes">
                        <ListSection type="header">
                            <ListRow grid={grids}>
                                {['Indústria', 'Data de registro','Período (mês)' ,'Opções'].map((column, index) => (
                                    <ListColumn key={index} text={column} />
                                ))}
                            </ListRow>
                        </ListSection>
                        <ListSection type="main">
                            {SHOW_LOADING && (
                                <ListRow grid="1fr">
                                    <ListColumn center>
                                        <CircleSpinner size={32} color="grey" />
                                    </ListColumn>
                                </ListRow>
                            )}
                            {SHOW_LISTA &&
                                listaIndustria.map((item, index) => (
                                    <ListRow
                                        key={item.id ?? index}
                                        zebra={index % 2 === 0}
                                        grid={grids}
                                    >
                                        <ListColumn text={item.nome} />
                                        <ListColumn text={item.dtRegistro ? new Date(item.dtRegistro).toLocaleDateString() : '-'} />
                                        <ListColumn text={item.descricaoPessoal ?? '-'} />
                                        <ListColumn center>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                                                <ListOption
                                                    icon={mdiMinusCircleOutline}
                                                    status="error"
                                                    tooltip="Remover da lista"
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        handleOpenConfirmRemover(item)
                                                    }}
                                                />
                                                <ListOption
                                                    icon={mdiCogOutline}
                                                    tooltip="Parâmetros"
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        handleOpenParametros(item)
                                                    }}
                                                />
                                            </div>
                                        </ListColumn>
                                    </ListRow>
                                ))}
                        </ListSection>
                    </List>
                </SectionBox>
            </Section>

            <Modal
                open={openModal}
                setOpen={setOpenModal}
                width="900px"
                title="Selecionar Clentes"
                actions={[]}
            >
                {requestGet.loading && (
                    <div className="center" style={{ padding: '16px 0' }}>
                        <CircleSpinner size={32} color="grey" />
                    </div>
                )}
                {!requestGet.loading && (
                    <div
                        style={{
                            paddingTop: '4px',
                            minHeight: 260,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start'
                        }}
                    >

                        <MagicSelectForm
                            options={optionsPessoas}
                            selected={selectedPessoas}
                            setSelected={setSelectedPessoas}
                            placeholder="Selecione os clientes para monitoramento frequente..."
                            onAdd={handleAddSelecionados}
                            addTooltip="Adicionar"
                        />
                    </div>
                )}
            </Modal>

            <Modal
                open={openParametrosModal}
                setOpen={setOpenParametrosModal}
                width="700px"
                title={
                    clienteParametroSelecionado
                        ? `Parâmetros de ${clienteParametroSelecionado.nome}`
                        : 'Parâmetros da pessoa'
                }
                actions={[]}
            >
                {clienteParametroSelecionado && (
                    <PessoaParametroForm
                        key={clienteParametroSelecionado.id}
                        pessoa={clienteParametroSelecionado}
                        onSaved={async () => {
                            setOpenParametrosModal(false)
                            setClienteParametroSelecionado(null)
                            await reloadMonitoramento()
                            await getClients('PessoaMonitoramentoFrequente', dispatch)
                        }}
                    />
                )}
            </Modal>

            <Modal
                open={openConfirmModal}
                setOpen={setOpenConfirmModal}
                width="480px"
                title="Remover monitoramento frequente"
                actions={[
                    {
                        label: 'Cancelar',
                        theme: 'outline',
                        onClick: handleCancelRemover
                    },
                    {
                        label: 'Remover',
                        theme: 'default',
                        onClick: handleConfirmRemover
                    }
                ]}
            >
                <p style={{ fontSize: '0.9rem' }}>
                    Tem certeza que deseja remover{' '}
                    <strong>{clienteSelecionado?.nome}</strong> da lista de indústrias
                    monitoradas frequentemente?
                </p>
            </Modal>
        </>
    )
}

export default IndustriasMonitoradasFrequentes


